import { createClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";
import { IMPERSONATION_AUTH_KEY, readImpersonation } from "../auth/impersonation-storage";

const url = import.meta.env.VITE_SUPABASE_URL;
// Supabase a renommé "anon key" en "publishable key" mi-2024.
// On accepte les deux noms pour faciliter les setups existants.
const env = import.meta.env as ImportMetaEnv & {
  VITE_SUPABASE_ANON_KEY?: string;
};
const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY ?? env.VITE_SUPABASE_ANON_KEY;

if (!url || !publishableKey) {
  // En dev, lever l'erreur plus tôt aide à diagnostiquer.
  const message =
    "[supabase] Variables d'environnement manquantes. " +
    "Renseigne VITE_SUPABASE_URL et VITE_SUPABASE_PUBLISHABLE_KEY dans .env " +
    "(voir .env.example).";
  if (import.meta.env.DEV) {
    throw new Error(message);
  }
  console.warn(message);
}

const impersonation = readImpersonation();
const isolated = impersonation?.isolated === true;

export function createIsolatedAuthClient(accessToken?: string) {
  return createClient<Database>(url ?? "", publishableKey ?? "", {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    ...(accessToken ? { global: { headers: { Authorization: `Bearer ${accessToken}` } } } : {}),
  });
}

export const supabase = createClient<Database>(url ?? "", publishableKey ?? "", {
  auth: {
    persistSession: true,
    autoRefreshToken: !isolated,
    detectSessionInUrl: !isolated,
    ...(isolated
      ? { storage: sessionStorage, storageKey: impersonation?.storageKey ?? IMPERSONATION_AUTH_KEY }
      : {}),
    // Flow "implicit" : le token revient dans le hash de l'URL côté
    // /auth/callback. Compatible avec les liens générés via
    // `auth.admin.generateLink({ type: "invite" | "magiclink" })` qui
    // utilisent toujours ce format (cf. log Supabase Auth :
    // "login_method":"implicit"). Le flow PKCE n'est pas adapté ici car
    // ces endpoints ne génèrent pas de code_verifier côté client.
    flowType: "implicit",
  },
  global: {
    headers: {
      "x-application-name": "noaretraite-web",
    },
  },
});

// =============================================================================
// Ré-exports typés (alias pratiques au-dessus des types Supabase générés)
// =============================================================================
export type { Database };
export type { Tables, TablesInsert, TablesUpdate, Enums, Json } from "./database.types";

import type { Enums } from "./database.types";

// Enums applicatifs — alias nommés au-dessus des Enums<"…"> générés par Supabase
export type UserRole = Enums<"user_role">;
export type DossierStatus = Enums<"dossier_status">;
export type BriType = Enums<"bri_type">;
export type ShareAuthorization = Enums<"share_authorization">;
export type SalaryBand = Enums<"salary_band">;
export type ExitType = Enums<"exit_type">;
export type Gender = Enums<"gender">;
export type MaritalStatus = Enums<"marital_status">;
export type ProfessionalCategory = Enums<"professional_category">;
export type DocumentKind = Enums<"document_kind">;
export type OperationStatus = Enums<"operation_status">;
export type MessageChannel = Enums<"message_channel">;
export type AppointmentStatus = Enums<"appointment_status">;
export type AppointmentChannel = Enums<"appointment_channel">;
export type HarvestStudyStatus = Enums<"harvest_study_status">;
export type RgpdRequestKind = Enums<"rgpd_request_kind">;
export type RgpdRequestStatus = Enums<"rgpd_request_status">;
